export type SavePhase = 'idle' | 'saving' | 'saved' | 'offline';

export function deriveSavePhase(input: { online: boolean; dirty: boolean; confirmed: boolean }): SavePhase {
  if (!input.online) return 'offline';
  if (input.dirty) return 'saving';
  if (input.confirmed) return 'saved';
  return 'idle';
}

export function saveStatusText(phase: SavePhase, savedAt?: string | null): string | null {
  if (phase === 'offline') return 'Sin conexión, guardado en este dispositivo';
  if (phase === 'saving') return 'Guardando…';
  if (phase !== 'saved') return null;
  if (!savedAt) return 'Guardado ✓';
  const date = new Date(savedAt);
  if (Number.isNaN(date.getTime())) return 'Guardado ✓';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `Guardado ✓ ${hh}:${mm}`;
}

export interface AttemptConnection {
  readonly online: boolean;
  readonly epoch: number;
  subscribe(listener: () => void): () => void;
  markOnline(): void;
  markOffline(): void;
  begin(): number;
  report(status: number, startedEpoch: number): void;
}

/**
 * One shared online flag. `epoch` advances on every 'online' and every OK response,
 * so a network error from a request that started earlier cannot put the banner back.
 */
export function createAttemptConnection(initialOnline = true): AttemptConnection {
  let online = initialOnline;
  let epoch = 0;
  const listeners = new Set<() => void>();

  function emit() {
    for (const listener of listeners) listener();
  }

  return {
    get online() {
      return online;
    },
    get epoch() {
      return epoch;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    markOnline() {
      epoch += 1;
      if (online) return;
      online = true;
      emit();
    },
    markOffline() {
      if (!online) return;
      online = false;
      emit();
    },
    begin() {
      return epoch;
    },
    report(status, startedEpoch) {
      if (status > 0) {
        epoch += 1;
        if (!online) {
          online = true;
          emit();
        }
        return;
      }
      if (status === 0 && startedEpoch === epoch && online) {
        online = false;
        emit();
      }
    },
  };
}

function bindWindow(connection: AttemptConnection) {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => connection.markOnline());
  window.addEventListener('offline', () => connection.markOffline());
}

export const attemptConnection = createAttemptConnection(
  typeof navigator === 'undefined' ? true : navigator.onLine,
);
bindWindow(attemptConnection);
